import { ToolbarSearch } from '../../../sharedComponents/filter/ToolbarSearch';
import {
    getServiceOrderCategory,
    getServiceOrderSearch,
    getServiceOrderSearchMode,
    ServiceOrderSearchMode,
    serviceOrdersSlice,
    setServiceOrderSearch,
} from '../../../reducers/serviceOrdersSlice';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { ServiceCategory } from '../commonServiceOrder.types';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useIntl } from 'react-intl';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import { clientSideFeatureToggles } from '../../../../../configuration/featureToggle/clientSideFeatureToggles';

const DOCUMENT_ID_LENGTH = 35;

const ServiceOptionSearch = () => (
    <ToolbarSearch
        getSearchTerm={getServiceOrderSearch}
        setSearchTerm={setServiceOrderSearch}
        tooltipMessageId="outboundPortal.serviceOrders.table.toolbar.search.placeholder.tooltip"
        placeholderMessageId="outboundPortal.serviceOrders.table.toolbar.search.placeholder"
        maxLengthInput={DOCUMENT_ID_LENGTH}
    />
);

const ServiceOrderSearch = () => {
    type SearchOption = SelectOption & {
        id: ServiceOrderSearchMode;
        placeholder: string;
    };

    const intl = useIntl();
    const searchMode = useAppSelector(getServiceOrderSearchMode);
    const searchTerm = useAppSelector(getServiceOrderSearch);
    const dispatch = useAppDispatch();

    const selectOptions: SearchOption[] = useMemo(
        () =>
            Object.values(ServiceOrderSearchMode).map((mode) => ({
                id: mode,
                label: intl.formatMessage({
                    id: `outboundOrderBook.serviceOrder.table.toolbar.search.mode.${mode}.label`,
                }),
                placeholder: intl.formatMessage({
                    id: `outboundOrderBook.serviceOrder.table.toolbar.search.mode.${mode}.placeholder`,
                }),
            })),
        [intl],
    );

    const onSearchModeChange = useCallback(
        (option: SearchOption | undefined) => {
            if (option !== undefined) {
                dispatch(serviceOrdersSlice.actions.setSearchMode(option.id));
            }
        },
        [dispatch],
    );

    const onSearchTermChange = useCallback(
        (newValue: string) => dispatch(setServiceOrderSearch({ value: newValue })),
        [dispatch],
    );

    const onClear = useCallback(() => dispatch(setServiceOrderSearch({ value: '', immediate: true })), [dispatch]);

    const onKeyPress = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                dispatch(setServiceOrderSearch({ value: searchTerm, immediate: true }));
            }
        },
        [searchTerm, dispatch],
    );

    const placeholder = selectOptions.find(({ id }) => id === searchMode)?.placeholder;

    return (
        // The ClearableInput CSS supports buttons at the end but not at the start.
        // If one tries to add buttons on the start the input is still rounded on
        // that side. Therefore, the code uses the render prop to build a nested
        // input group with a select and an input like shown in the UI KIT:
        // https://uikit.developers.rio.cloud/#/components/select
        <ClearableInput
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={onSearchTermChange}
            onClear={onClear}
            onKeyPress={onKeyPress}
        >
            {(inputProps) => {
                return (
                    <span className="input-group width-500">
                        <span className="input-group-btn">
                            <Select
                                className="width-150"
                                options={selectOptions}
                                value={[searchMode]}
                                onChange={onSearchModeChange}
                            />
                        </span>
                        <input {...inputProps} />
                    </span>
                );
            }}
        </ClearableInput>
    );
};

export const ServiceOrderToolbarSearch = () => {
    // TODO PORTALOUT-3925 Remove this "hack":
    //  The service option tab keeps the old search bar, thus I renamed it already.
    //  I always show it when the feature toggle is off
    const useNewSearch = clientSideFeatureToggles.newServiceOrderSearch;
    const category = useAppSelector(getServiceOrderCategory);

    return category === ServiceCategory.OPTIONS || !useNewSearch ? <ServiceOptionSearch /> : <ServiceOrderSearch />;
};
